import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { authClient, signOut, useSession } from "@/lib/auth-client";
import { describeDevice } from "@/lib/device";
import { tools } from "@/lib/tools";
import { useUiStore } from "@/stores/ui";

export const Route = createFileRoute("/_authed/einstellungen/konto")({
  component: Account,
});

const failed = "Das hat nicht geklappt. Bitte versuchen Sie es erneut.";
const card = "space-y-4 rounded-xl border p-6";

function Account() {
  const navigate = useNavigate();
  return (
    <div className="space-y-6">
      <Profile />
      <Password />
      <Appearance />
      <Devices />
      <Button
        variant="outline"
        onClick={async () => {
          await signOut();
          await navigate({ to: "/login" });
        }}
      >
        <LogOut aria-hidden className="size-4" />
        Abmelden
      </Button>
    </div>
  );
}

const profileInput = z.object({
  name: z.string().trim().min(1, "Pflichtfeld").max(200, "Höchstens 200 Zeichen"),
});

function Profile() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string | null>(null);
  const form = useForm<z.infer<typeof profileInput>>({
    resolver: zodResolver(profileInput),
    values: { name: session?.user.name ?? "" },
  });

  const onSubmit = form.handleSubmit(async ({ name }) => {
    setStatus(null);
    const { error } = await authClient.updateUser({ name });
    if (error) {
      setStatus(failed);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: tools.queryKey("GET /api/me") });
    setStatus("Gespeichert.");
  });

  return (
    <section aria-labelledby="profil" className={card}>
      <h2 id="profil" className="font-semibold">
        Profil
      </h2>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="name" error={form.formState.errors.name?.message}>
            <Input id="name" autoComplete="name" {...form.register("name")} />
          </Field>
          <Field label="E-Mail" htmlFor="email">
            {/* No mailer is wired yet, so a new address could not be confirmed. */}
            <Input
              id="email"
              type="email"
              value={session?.user.email ?? ""}
              readOnly
              aria-describedby="email-hint"
              className="bg-muted text-muted-foreground"
            />
            <p id="email-hint" className="text-muted-foreground text-xs">
              Ihre Anmeldeadresse. Zum Ändern wenden Sie sich an uns.
            </p>
          </Field>
        </div>
        <div className="flex items-center justify-end gap-3">
          {status ? (
            <p role="status" className="text-muted-foreground text-sm">
              {status}
            </p>
          ) : null}
          <Button type="submit" disabled={form.formState.isSubmitting}>
            Speichern
          </Button>
        </div>
      </form>
    </section>
  );
}

const passwordInput = z
  .object({
    currentPassword: z.string().min(1, "Pflichtfeld"),
    newPassword: z.string().min(8, "Mindestens 8 Zeichen").max(128, "Höchstens 128 Zeichen"),
    repeat: z.string(),
    revokeOtherSessions: z.boolean(),
  })
  .refine((values) => values.newPassword === values.repeat, {
    path: ["repeat"],
    message: "Die Passwörter stimmen nicht überein.",
  });

function Password() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<{ ok: boolean; message: string } | null>(null);
  const form = useForm<z.infer<typeof passwordInput>>({
    resolver: zodResolver(passwordInput),
    defaultValues: { currentPassword: "", newPassword: "", repeat: "", revokeOtherSessions: true },
  });
  const { errors } = form.formState;

  const onSubmit = form.handleSubmit(
    async ({ currentPassword, newPassword, revokeOtherSessions }) => {
      setStatus(null);
      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions,
      });
      if (error) {
        if (error.code === "INVALID_PASSWORD")
          form.setError("currentPassword", { message: "Das aktuelle Passwort stimmt nicht." });
        else setStatus({ ok: false, message: failed });
        return;
      }
      form.reset();
      await queryClient.invalidateQueries({ queryKey: sessionsKey });
      setStatus({ ok: true, message: "Passwort geändert." });
    },
  );

  return (
    <section aria-labelledby="passwort" className={card}>
      <h2 id="passwort" className="font-semibold">
        Passwort
      </h2>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {/* Password managers file the new password under the username in the same form. */}
        <input
          type="email"
          name="username"
          autoComplete="username"
          value={session?.user.email ?? ""}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Aktuelles Passwort"
            htmlFor="current-password"
            error={errors.currentPassword?.message}
            className="sm:col-span-2 sm:max-w-[calc(50%-0.5rem)]"
          >
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              {...form.register("currentPassword")}
            />
          </Field>
          <Field label="Neues Passwort" htmlFor="new-password" error={errors.newPassword?.message}>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              {...form.register("newPassword")}
            />
          </Field>
          <Field label="Neues Passwort wiederholen" htmlFor="repeat" error={errors.repeat?.message}>
            <Input
              id="repeat"
              type="password"
              autoComplete="new-password"
              {...form.register("repeat")}
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              {...form.register("revokeOtherSessions")}
            />
            Auf allen anderen Geräten abmelden
          </label>
          <div className="flex items-center gap-3">
            {status ? (
              <p
                role={status.ok ? "status" : "alert"}
                className={status.ok ? "text-muted-foreground text-sm" : "text-destructive text-sm"}
              >
                {status.message}
              </p>
            ) : null}
            <Button type="submit" variant="outline" disabled={form.formState.isSubmitting}>
              Passwort ändern
            </Button>
          </div>
        </div>
      </form>
    </section>
  );
}

const themes = [
  { value: "light", label: "Hell" },
  { value: "dark", label: "Dunkel" },
  { value: "system", label: "Wie das System" },
] as const;

function Appearance() {
  const theme = useUiStore((state) => state.theme);
  const setTheme = useUiStore((state) => state.setTheme);
  return (
    <section className={card}>
      <fieldset className="space-y-4">
        <legend className="space-y-1">
          <span className="block font-semibold">Darstellung</span>
          {/* The choice is kept in this browser's storage (`stores/ui.ts`), not the account. */}
          <span className="block text-muted-foreground text-sm">Gilt für diesen Browser.</span>
        </legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {themes.map(({ value, label }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm has-checked:border-primary has-checked:ring-1 has-checked:ring-primary has-focus-visible:ring-2 has-focus-visible:ring-accent"
            >
              <input
                type="radio"
                name="theme"
                value={value}
                checked={theme === value}
                onChange={() => setTheme(value)}
                className="size-4 accent-primary"
              />
              {label}
            </label>
          ))}
        </div>
      </fieldset>
    </section>
  );
}

const sessionsKey = ["auth", "sessions"] as const;

function Devices() {
  const { data: current } = useSession();
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: sessionsKey,
    queryFn: async () => {
      const { data, error } = await authClient.listSessions();
      if (error) throw new Error(error.message ?? failed);
      return data;
    },
  });
  const settle = () => queryClient.invalidateQueries({ queryKey: sessionsKey });
  const revoke = useMutation({
    mutationFn: async (token: string) => {
      const { error } = await authClient.revokeSession({ token });
      if (error) throw new Error(error.message ?? failed);
    },
    onSettled: settle,
  });
  const revokeOthers = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.revokeOtherSessions();
      if (error) throw new Error(error.message ?? failed);
    },
    onSettled: settle,
  });

  const currentId = current?.session.id;
  // This device first, then the others newest first.
  const rows = [...(sessions.data ?? [])].sort(
    (a, b) =>
      Number(b.id === currentId) - Number(a.id === currentId) ||
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const error = sessions.error ?? revoke.error ?? revokeOthers.error;

  return (
    <section aria-labelledby="geraete" className="rounded-xl border">
      <div className="flex flex-wrap items-center justify-between gap-3 p-6 pb-4">
        <h2 id="geraete" className="font-semibold">
          Angemeldete Geräte
        </h2>
        {rows.length > 1 ? (
          <Button
            variant="outline"
            size="sm"
            disabled={revokeOthers.isPending}
            onClick={() => revokeOthers.mutate()}
          >
            Alle anderen abmelden
          </Button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="px-6 pb-4 text-destructive text-sm">
          {error.message}
        </p>
      ) : null}
      <ul>
        {rows.map((session) => (
          <li key={session.id} className="flex items-center gap-3 border-t px-6 py-3">
            <div className="min-w-0 flex-1 text-sm">
              <p className="truncate font-medium">{describeDevice(session.userAgent)}</p>
              <p className="text-muted-foreground text-xs">
                {session.id === currentId ? "Dieses Gerät · " : ""}
                angemeldet seit {new Date(session.createdAt).toLocaleDateString("de-DE")}
              </p>
            </div>
            {session.id === currentId ? null : (
              <Button
                variant="ghost"
                size="sm"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate(session.token)}
              >
                Abmelden
              </Button>
            )}
          </li>
        ))}
      </ul>
      {/* `auth.ts` caches a session in its cookie for five minutes. */}
      <p className="border-t px-6 py-3 text-muted-foreground text-xs">
        Ein abgemeldetes Gerät kann bis zu fünf Minuten weiterarbeiten.
      </p>
    </section>
  );
}
